import { Box, Group, MultiSelect, Select, Switch, TextInput } from "@mantine/core"
import { DatePickerInput } from "@mantine/dates"
import { MagnifyingGlassIcon } from "@phosphor-icons/react"
import { useTranslation } from "react-i18next"

export type PendingQuickFilter = "ALL" | "VIP_URGENT" | "SLA_RISK" | "BIG_CARGO" | "CUSTOMER_PICKUP"

interface Props {
  channel: string
  onChannelChange: (value: string) => void
  channelCounts: { all: number; local: number; supply: number; pickup: number }
  orderType: string
  onOrderTypeChange: (value: string) => void
  orderTypeOptions: { value: string; label: string }[]
  quick: PendingQuickFilter
  onQuickChange: (next: PendingQuickFilter) => void
  counts: {
    all: number
    vipUrgent: number
    slaRisk: number
    bigCargo: number
    customerPickup: number
  }
  dateRange: [Date | null, Date | null]
  onDateRangeChange: (value: [Date | null, Date | null]) => void
  districtOptions: string[]
  selectedDistricts: string[]
  onDistrictsChange: (value: string[]) => void
  groupByDistrict: boolean
  onGroupByDistrictChange: (value: boolean) => void
  search: string
  onSearchChange: (value: string) => void
  onClear: () => void
}

/** One row of labelled filters (canvas Ord_DeliveryHub): Суваг, Төрөл, Яаралт байдал, Огноо, Бүс, Хайх + group toggle. */
export function PendingFiltersToolbar({
  channel,
  onChannelChange,
  channelCounts,
  orderType,
  onOrderTypeChange,
  orderTypeOptions,
  quick,
  onQuickChange,
  counts,
  dateRange,
  onDateRangeChange,
  districtOptions,
  selectedDistricts,
  onDistrictsChange,
  groupByDistrict,
  onGroupByDistrictChange,
  search,
  onSearchChange,
}: Props) {
  const { t } = useTranslation("delivery")

  const channelData = [
    { value: "ALL", label: t("manage.filter.channelAll", { n: channelCounts.all }) },
    { value: "LOCAL_DELIVERY", label: `${t("badges.channel.LOCAL_DELIVERY")} (${channelCounts.local})` },
    { value: "SUPPLY_FREIGHT", label: `${t("badges.channel.SUPPLY_FREIGHT")} (${channelCounts.supply})` },
    { value: "CUSTOMER_PICKUP", label: `${t("badges.channel.CUSTOMER_PICKUP")} (${channelCounts.pickup})` },
  ]

  const quickData = [
    { value: "ALL", label: t("manage.filter.quickAll", { n: counts.all }) },
    { value: "VIP_URGENT", label: t("manage.filter.quickUrgent", { n: counts.vipUrgent }) },
    { value: "SLA_RISK", label: t("manage.filter.quickSla", { n: counts.slaRisk }) },
    { value: "BIG_CARGO", label: t("manage.filter.quickBig", { n: counts.bigCargo }) },
  ]

  return (
    <Group gap="md" wrap="wrap" align="flex-end">
      <Select
        label={t("manage.filter.channel")}
        data={channelData}
        value={channel}
        onChange={(v) => onChannelChange(v ?? "ALL")}
        w={170}
        allowDeselect={false}
      />
      <Select
        label={t("manage.filter.type")}
        data={orderTypeOptions}
        value={orderType}
        onChange={(v) => onOrderTypeChange(v ?? "ALL")}
        w={170}
        allowDeselect={false}
      />
      <Select
        label={t("manage.filter.urgency")}
        data={quickData}
        value={quick}
        onChange={(v) => onQuickChange((v as PendingQuickFilter) ?? "ALL")}
        w={220}
        allowDeselect={false}
      />
      <DatePickerInput
        type="range"
        label={t("manage.filter.date")}
        placeholder={t("manage.filter.datePlaceholder")}
        value={dateRange}
        onChange={(value) => {
          const [s, e] = value as [unknown, unknown]
          onDateRangeChange([s ? new Date(s as string) : null, e ? new Date(e as string) : null])
        }}
        valueFormat="YYYY-MM-DD"
        w={230}
        clearable
      />
      <MultiSelect
        label={t("manage.filter.zone")}
        placeholder={selectedDistricts.length === 0 ? t("manage.filter.all") : undefined}
        data={districtOptions}
        value={selectedDistricts}
        onChange={onDistrictsChange}
        w={190}
        searchable
        clearable
        hidePickedOptions
        nothingFoundMessage={t("manage.filter.noZone")}
      />
      <Box flex={1} miw={200}>
        <TextInput
          label={t("manage.filter.search")}
          placeholder={t("manage.filter.searchPlaceholder")}
          value={search}
          onChange={(e) => onSearchChange(e.currentTarget.value)}
          leftSection={<MagnifyingGlassIcon size={16} />}
        />
      </Box>
      <Switch
        mb={8}
        label={t("manage.filter.groupByZone")}
        checked={groupByDistrict}
        onChange={(e) => onGroupByDistrictChange(e.currentTarget.checked)}
      />
    </Group>
  )
}
